import React, { useState, useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';

// Estímulo à instalação do PWA em celular.
// - Android/Chrome: captura `beforeinstallprompt` e oferece botão "Instalar".
// - iOS/Safari: mostra instruções manuais (Compartilhar → Adicionar à Tela de Início).
// Aparece só em mobile, fora do modo standalone, e respeita dispensa (localStorage).
const DISMISS_KEY = 'simby_pwa_dismissed';

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isMobile = () =>
  /android|iphone|ipad|ipod/i.test(window.navigator.userAgent);

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || !isMobile()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    // Android: o evento dá controle sobre o prompt nativo
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS não dispara o evento — mostramos instruções manuais após um instante
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setVisible(true), 1500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    setShowIosHelp(false);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try { await deferredPrompt.userChoice; } catch { /* ignore */ }
      setDeferredPrompt(null);
      dismiss();
    } else if (isIOS()) {
      setShowIosHelp(true);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[60] md:left-auto md:right-6 md:max-w-sm">
      <div className="bg-white rounded-2xl shadow-2xl border-2 border-purple-200 p-4">
        <div className="flex items-start gap-3">
          <img src="/images/simby-mascote.png" alt="Simby" className="w-9 h-12 object-contain flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 text-sm">Instale a Simby no seu celular</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Acesso rápido, em tela cheia, direto da tela inicial.
            </p>

            {showIosHelp ? (
              <div className="mt-3 text-xs text-gray-700 bg-purple-50 rounded-xl p-3 flex items-start gap-2">
                <Share className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>
                  Toque em <strong>Compartilhar</strong> na barra do Safari e escolha
                  <strong> "Adicionar à Tela de Início"</strong>.
                </span>
              </div>
            ) : (
              <button
                onClick={handleInstall}
                className="mt-3 inline-flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors"
              >
                <Download className="w-4 h-4" />
                {isIOS() && !deferredPrompt ? 'Como instalar' : 'Instalar'}
              </button>
            )}
          </div>
          <button onClick={dismiss} className="text-gray-400 hover:text-gray-600 flex-shrink-0" aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
